import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';
import { formatCurrency } from '../utils/currency';
import { TimePeriod, DashboardView } from '../types/dashboard';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types/auth';
import { useProvider } from '../contexts/ProviderContext';
import { MetricCard } from '../components/dashboard/MetricCard';
import { SpendChart } from '../components/dashboard/SpendChart';
import { AnomalyList } from '../components/dashboard/AnomalyList';
import { WasteBreakdown } from '../components/dashboard/WasteBreakdown';
import { CommitmentMetrics } from '../components/dashboard/CommitmentMetrics';
import { SavingsBreakdown } from '../components/dashboard/SavingsBreakdown';
import { CostByRegion } from '../components/dashboard/CostByRegion';
import { CostByService } from '../components/dashboard/CostByService';
import { CostByAccount } from '../components/dashboard/CostByAccount';
import { CostByProvider } from '../components/dashboard/CostByProvider';
import { ProviderSummaryCards, ProviderMetricsData } from '../components/dashboard/ProviderSummaryCards';
import { ProviderBreakdownBar } from '../components/dashboard/ProviderBreakdownBar';
import { ProviderEmptyState } from '../components/dashboard/ProviderEmptyState';
import { StaleDataBanner } from '../components/common/StaleDataBanner';
import { ErrorState } from '../components/common/ErrorState';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FilterSelect } from '../components/common/FilterSelect';
import { ProviderTiles } from '../components/common/ProviderTiles';
import { CalendarDays, RefreshCw } from 'lucide-react';
import { withFreshData, authHeaders } from '../services/apiHeaders';
import { lifecycleApi } from '../components/recommendations/RecommendationLifecycle';

/**
 * Which sections render for each dashboard view (Req 76 AC2). Previously
 * `view` was tracked in state and even sent to getDashboard() (the unused
 * legacy fetch path), but the actual data-fetching function the page calls,
 * getMockDashboard(), never read it at all - every view rendered an
 * identical page. There was also no way to reach ENGINEERING/FINANCE at
 * all, since the role->view effect below only ever produced CFO or FINOPS.
 * This maps each view's declared framing (executive summary / operational
 * detail / service-scoped / allocation-chargeback) to which existing
 * sections are relevant, rather than fetching different data per view.
 */
const VIEW_SECTIONS: Record<DashboardView, {
  keyMetricCards: boolean;
  providerSummaryCards: boolean;
  providerBreakdownBar: boolean;
  spendTrendChart: boolean;
  savingsBreakdown: boolean;
  wasteBreakdown: boolean;
  costByRegion: boolean;
  costByProvider: boolean;
  costByService: boolean;
  costByAccount: boolean;
  commitmentMetrics: boolean;
  topAnomalies: boolean;
}> = {
  // Executive summary - the top-line metrics Req 76 AC1 enumerates, nothing more granular.
  [DashboardView.CFO]: {
    keyMetricCards: true, providerSummaryCards: true, providerBreakdownBar: true,
    spendTrendChart: true, savingsBreakdown: true, wasteBreakdown: true,
    costByRegion: false, costByProvider: true, costByService: false, costByAccount: false,
    commitmentMetrics: true, topAnomalies: true,
  },
  // Operational detail - everything, including per-region/service/account breakdowns.
  [DashboardView.FINOPS]: {
    keyMetricCards: true, providerSummaryCards: true, providerBreakdownBar: true,
    spendTrendChart: true, savingsBreakdown: true, wasteBreakdown: true,
    costByRegion: true, costByProvider: true, costByService: true, costByAccount: true,
    commitmentMetrics: true, topAnomalies: true,
  },
  // Service-scoped - what's costing what and where waste/anomalies are, no
  // budget/financial framing (that's not an engineer's concern here).
  [DashboardView.ENGINEERING]: {
    keyMetricCards: false, providerSummaryCards: false, providerBreakdownBar: false,
    spendTrendChart: true, savingsBreakdown: false, wasteBreakdown: true,
    costByRegion: true, costByProvider: false, costByService: true, costByAccount: false,
    commitmentMetrics: false, topAnomalies: true,
  },
  // Allocation/chargeback - budget and spend attributed by account/provider,
  // not the operational service/region/waste detail FinOps or Engineering need.
  [DashboardView.FINANCE]: {
    keyMetricCards: true, providerSummaryCards: false, providerBreakdownBar: true,
    spendTrendChart: true, savingsBreakdown: true, wasteBreakdown: false,
    costByRegion: false, costByProvider: true, costByService: false, costByAccount: true,
    commitmentMetrics: true, topAnomalies: false,
  },
};


const TIME_PERIOD_OPTIONS: Array<{ value: TimePeriod; label: string }> = [
  { value: TimePeriod.LAST_7_DAYS, label: 'Last 7 Days' },
  { value: TimePeriod.LAST_30_DAYS, label: 'Last 30 Days' },
  { value: TimePeriod.LAST_90_DAYS, label: 'Last 90 Days' },
  { value: TimePeriod.LAST_12_MONTHS, label: 'Last 12 Months' },
  { value: TimePeriod.LAST_24_MONTHS, label: 'Last 24 Months' },
];

/**
 * Executive Dashboard Page
 * Implements Requirement 76: Web UI — Executive Dashboard [MVP]
 * 
 * AC 1: Provides executive dashboard with:
 *   - Total spend vs budget
 *   - Month-over-month trend
 *   - Realized savings
 *   - Forecast accuracy
 *   - Commitment coverage/utilization
 *   - Waste index
 *   - Top anomalies
 * 
 * AC 2: Supports role-based views (CFO, FinOps, Engineering, Finance)
 * AC 3: Optimized for P95 ≤ 2 seconds for 90-day scope
 * AC 4: WCAG 2.1 AA compliant
 */
export const DashboardPage: React.FC = () => {
  const forecastingEnabled = useFeatureFlag('forecasting_enabled');
  const { user } = useAuthStore();
  const { provider: selectedProvider, setProvider: setSelectedProvider } = useProvider();
const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.LAST_90_DAYS);
  const [view, setView] = useState<DashboardView>(DashboardView.CFO);

  // Determine default view based on user role (AC 2: role-based views)
  useEffect(() => {
    if (user?.roles.includes(UserRole.ADMIN)) {
      setView(DashboardView.CFO);
    } else if (user?.roles.includes(UserRole.ANALYST)) {
      setView(DashboardView.FINOPS);
    } else {
      setView(DashboardView.CFO);
    }
  }, [user]);

  // Phase 4.0 (gap 11): money the BILL has proven saved (Verified +
  // Resolved, measured) - distinct from "Potential Savings" (identified).
  // Read from optimization-api's lifecycle buckets; a failure just hides the
  // line instead of showing a misleading $0.
  const { data: savedBucket } = useQuery({
    queryKey: ['recommendation-lifecycle-saved', selectedProvider],
    queryFn: async () => {
      const params = selectedProvider && selectedProvider !== 'all' ? { provider: selectedProvider } : {};
      const res = await lifecycleApi.get('/api/v1/recommendations/summary', { params, headers: authHeaders() });
      return (res.data?.data?.lifecycle?.saved as { count: number; saved_monthly: number } | undefined) ?? null;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Fetch dashboard data with React Query for caching and error handling
  // REQ-012: Include provider in query key so data refetches when provider filter changes
  const {
    data: dashboard,
    isLoading,
    isError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['dashboard', timePeriod, view, selectedProvider],
    queryFn: () =>
      dashboardService.getMockDashboard({
        timePeriod,
        view,
        provider: selectedProvider === 'all' ? undefined : selectedProvider,
      }),
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // 5 minutes
  });

  /**
   * Build per-provider metrics from available dashboard data.
   * Derives totalCost, MoM trend, waste %, savings, top services, and connection status
   * for each provider (AWS, Azure, GCP) from the costByProvider breakdown and other metrics.
   * REQ-012-HYBRID: Provider summary cards display all key metrics per cloud provider.
   */
  // REQ-012-HYBRID: Fetch per-provider summary data from the dedicated API endpoint
  const { data: providerSummaryData } = useQuery({
    queryKey: ['providerSummary', timePeriod],
    queryFn: () =>
      dashboardService.getProviderSummary({ timePeriod, view }),
    staleTime: 60000,
    refetchInterval: 300000,
  });

  const providerMetrics = useMemo((): Partial<Record<'aws' | 'azure' | 'gcp', ProviderMetricsData>> | undefined => {
    // If per-provider API data is available, use it directly (real data from API)
    if (providerSummaryData?.providers) {
      const metrics: Partial<Record<'aws' | 'azure' | 'gcp', ProviderMetricsData>> = {};
      const validKeys: Array<'aws' | 'azure' | 'gcp'> = ['aws', 'azure', 'gcp'];

      for (const key of validKeys) {
        const providerData = providerSummaryData.providers[key];
        if (!providerData) continue;

        metrics[key] = {
          totalCost: providerData.cost,
          momTrendPercentage: Math.abs(providerData.mom_change),
          momTrendDirection: providerData.mom_trend,
          wastePercentage: providerData.waste_percentage,
          savingsOpportunity: providerData.savings_opportunity,
          topServices: providerData.top_services.map(s => ({ name: s.service, cost: s.cost })),
          connected: providerData.connection_status === 'connected',
          currency: providerData.currency as 'USD' | 'INR' | 'EUR', // CRITICAL: Include currency from API
        };
        
        console.log(`[DashboardPage] Mapped provider ${key}:`, {
          cost: providerData.cost,
          currency: providerData.currency,
          topServices: providerData.top_services.length
        });
      }

      return Object.keys(metrics).length > 0 ? metrics : undefined;
    }

    // Fallback: derive from costByProvider if provider-summary API is unavailable
    if (!dashboard?.costByProvider || dashboard.costByProvider.length === 0) {
      return undefined;
    }

    const overallMomPercentage = Math.abs(dashboard.momTrend.changePercentage);
    const overallMomDirection = dashboard.momTrend.trend === 'up' ? 'up' as const : 'down' as const;
    const overallWastePercentage = dashboard.wasteIndex.wastePercentage ?? 0;
    const overallSavings = dashboard.realizedSavings.totalSavings;

    const providerKeyMap: Record<string, 'aws' | 'azure' | 'gcp'> = {
      aws: 'aws',
      azure: 'azure',
      gcp: 'gcp',
      'amazon web services': 'aws',
      'microsoft azure': 'azure',
      'google cloud': 'gcp',
      'google cloud platform': 'gcp',
    };

    const topServices = dashboard.costByService?.slice(0, 9) ?? [];
    const metrics: Partial<Record<'aws' | 'azure' | 'gcp', ProviderMetricsData>> = {};

    for (const providerEntry of dashboard.costByProvider) {
      const key = providerKeyMap[providerEntry.provider.toLowerCase()];
      if (!key) continue;

      const providerCost = providerEntry.cost;
      const providerPercentage = providerEntry.percentage / 100;

      const momTrendPercentage = Math.round(overallMomPercentage * 10) / 10;
      const wastePercentage = Math.round(overallWastePercentage * 10) / 10;
      const savingsOpportunity = Math.round(overallSavings * providerPercentage);

      const providerIndex = key === 'aws' ? 0 : key === 'azure' ? 1 : 2;
      const providerTopServices = topServices
        .filter((_, i) => i % 3 === providerIndex)
        .slice(0, 3)
        .map(s => ({ name: s.service, cost: Math.round(s.cost * providerPercentage) }));

      const finalTopServices = providerTopServices.length > 0
        ? providerTopServices
        : [{ name: 'Compute', cost: Math.round(providerCost * 0.4) },
           { name: 'Storage', cost: Math.round(providerCost * 0.25) },
           { name: 'Database', cost: Math.round(providerCost * 0.15) }];

      metrics[key] = {
        totalCost: providerCost,
        momTrendPercentage,
        momTrendDirection: overallMomDirection,
        wastePercentage,
        savingsOpportunity,
        topServices: finalTopServices,
        connected: providerCost > 0,
        currency: key === 'azure' ? 'INR' : 'USD', // Fallback: Azure uses INR, others use USD
      };
    }

    return Object.keys(metrics).length > 0 ? metrics : undefined;
  }, [providerSummaryData, dashboard]);

  const formatCurrencyValue = (value: number) => {
    // Get currency from dashboard if available, otherwise default to USD
    const currency = (dashboard?.currency as 'USD' | 'INR') || 'USD';
    
    // Use our custom currency formatter to ensure correct symbols
    return formatCurrency(value, currency, false);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  /**
   * REQ-012 AC-012.9: Detect when a specific provider is selected but has no data.
   * When true, the dashboard shows a prominent ProviderEmptyState instead of metrics.
   */
  const selectedProviderHasNoData = useMemo(() => {
    if (selectedProvider === 'all') return false;

    // Check if provider metrics exist for the selected provider
    const metrics = providerMetrics?.[selectedProvider as 'aws' | 'azure' | 'gcp'];
    if (!metrics) return true;

    // If totalCost is 0 or provider is not connected, treat as no data
    if (metrics.totalCost === 0 && !metrics.connected) return true;

    return false;
  }, [selectedProvider, providerMetrics]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">Loading dashboard data…</p>
        {/* Skeleton metric cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="animate-pulse rounded-lg border border-border bg-secondary p-6">
              <div className="h-4 w-24 rounded bg-secondary mb-3"></div>
              <div className="h-8 w-32 rounded bg-muted-foreground/40 mb-2"></div>
              <div className="h-3 w-20 rounded bg-secondary"></div>
            </div>
          ))}
        </div>
        {/* Skeleton chart */}
        <div className="animate-pulse rounded-lg border border-border bg-secondary p-6 h-64">
          <div className="h-4 w-32 rounded bg-secondary mb-4"></div>
          <div className="flex items-end gap-2 h-40">
            {[40,70,55,80,65,90,72].map((h,i) => (
              <div key={i} className="flex-1 rounded bg-secondary" style={{height: `${h}%`}}></div>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 mt-4">
          <div className="h-2 w-48 rounded-full bg-primary overflow-hidden">
            <div className="h-full bg-primary animate-pulse" style={{width: '60%'}}></div>
          </div>
          <p className="text-sm text-muted-foreground">Fetching cost data from AWS and Azure — this takes 15–60 seconds</p>
        </div>

      </div>
    );
  }

  if (!dashboard) {
    return (
      <ErrorState
        title="Failed to load dashboard"
        message="Unable to fetch dashboard data. Please try again."
        onRetry={refetch}
      />
    );
  }

  const sections = VIEW_SECTIONS[view];

  return (
    /* No page-level padding here: MainLayout already wraps every route in
       `container mx-auto p-4 sm:p-6`, so this page was paying for it twice and
       losing ~48px of usable width - which is most of why the header row
       crushed together at laptop widths. */
    <div className="space-y-4">
      <StaleDataBanner dataUpdatedAt={dataUpdatedAt} isError={isError} onRetry={refetch} />

      {/* A provider showing $0.00 can mean "nothing onboarded" or "we could not
          read its credentials". The API distinguishes them in the envelope's
          `errors` array; nothing in the UI read it, so a broken provider was
          indistinguishable from an empty one. On 2026-10-07 an Azure 403
          reading its credential from Key Vault showed as Azure $0.00 with no
          explanation anywhere a user could see. This is that explanation. */}
      {(dashboard.dataWarnings ?? []).length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          {(dashboard.dataWarnings ?? []).map((w) => (
            <div key={w.code}>{w.message}</div>
          ))}
        </div>
      )}

      {/* Title lives in the app header (see NAVIGATION_CONFIG). This row keeps
          the two things the header can't carry: the live "last updated" stamp,
          and the period selector - right-aligned so it never competes with
          anything for width, which is what broke the earlier attempt. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(dashboard.lastUpdated).toLocaleString()}
          </p>
          {/* withFreshData makes this a REAL refetch: cost responses are cached
              server-side for an hour, so without the no-cache header this
              would re-render the identical payload. */}
          <button
            onClick={() => withFreshData(() => refetch())}
            disabled={isFetching}
            title="Refresh dashboard data"
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <FilterSelect<TimePeriod>
          label="Time period"
          icon={CalendarDays}
          value={timePeriod}
          onChange={setTimePeriod}
          align="end"
          options={TIME_PERIOD_OPTIONS}
        />
      </div>

      {/* Provider selector - centred tiles, the same shared component Cost
          Explorer, Recommendations and Anomalies use. Deliberately NOT the
          dropdown this page had before: four options that filter the whole
          page belong on the surface, not behind a click. */}
      <ProviderTiles value={selectedProvider} onChange={setSelectedProvider} />

      {/* The role/view selector (CFO / FinOps / Engineering / Finance) stays
          removed by request. `view` is still state and still drives
          VIEW_SECTIONS - it is now derived purely from the user's role by the
          effect above, rather than being switchable in the UI. */}

      {/* The "Viewing: Azure 🔵" banner that used to sit here is gone by
          request. It stated something the page already showed: selecting a
          provider highlights its tile above, so a second full-width block of
          solid provider colour saying the same thing only made the selection
          look over-highlighted. Clearing the filter is unaffected - the
          "All Providers" tile does it in one click, same as the × did. */}

      {/* REQ-012 AC-012.9: Show empty state when selected provider has no data */}
      {selectedProviderHasNoData && selectedProvider !== 'all' && (
        <ProviderEmptyState
          provider={selectedProvider as 'aws' | 'azure' | 'gcp'}
          className="mx-auto max-w-lg"
        />
      )}

      {/* Normal dashboard content - hidden when selected provider has no data */}
      {!selectedProviderHasNoData && (
        <>
      {/* Provider Summary Cards - REQ-012-HYBRID: 3-column grid at top of dashboard */}
      {sections.providerSummaryCards && (
        <ProviderSummaryCards
          activeProvider={selectedProvider}
          onProviderClick={setSelectedProvider}
          providerMetrics={providerMetrics}
        />
      )}

      {/* Provider Breakdown Bar - REQ-012-HYBRID: Visual cost split by provider */}
      {sections.providerBreakdownBar && dashboard.costByProvider && dashboard.costByProvider.length > 0 && (
        <ProviderBreakdownBar
          providers={dashboard.costByProvider}
          totalCost={dashboard.spendVsBudget.currentSpend}
          currency={dashboard.spendVsBudget.currency}
        />
      )}

      {/* Key Metrics Grid - AC 1: Total spend vs budget, MoM trend, savings, forecast accuracy */}
      {sections.keyMetricCards && (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Spend"
          // Kept to the one thing a reader cannot work out from the tile: that
          // the value and the budget line beneath it cover DIFFERENT periods.
          // The per-account mechanics that used to follow were accurate but
          // belong in Budget Settings, not in a tooltip on an executive summary.
          info="Total cloud cost across AWS, Azure and GCP for the period selected above. The budget line beneath is always THIS calendar month, which is a different period - so compare it against the MTD figure, not the tile value."
          value={formatCurrencyValue(dashboard.spendVsBudget.currentSpend)}
          subtitle={
            dashboard.spendVsBudget.budgetConfigured && dashboard.spendVsBudget.budgetAmount
              ? // Name the month. The tile's own value follows the period
                // selector, but the budget beneath it is always this month's -
                // without saying so, a 90-day total sitting above "Budget:
                // $10,000" reads as a single comparison when it is two
                // different periods.
                // Show the pacing, not just the raw MTD figure. "$199K MTD"
                // against "$330K/mo" invites the viewer to do the wrong
                // subtraction and conclude there is $131K of headroom, when on
                // day 16 of 30 the month is already running over pace. Naming
                // the day count makes the comparison self-explaining.
                `Budget: ${formatCurrencyValue(dashboard.spendVsBudget.budgetAmount)}/mo${
                  dashboard.spendVsBudget.budgetPeriodSpend != null
                    ? ` · ${formatCurrencyValue(dashboard.spendVsBudget.budgetPeriodSpend)} MTD${
                        dashboard.spendVsBudget.daysElapsed && dashboard.spendVsBudget.daysInMonth
                          ? ` (day ${dashboard.spendVsBudget.daysElapsed}/${dashboard.spendVsBudget.daysInMonth})`
                          : ''
                      }`
                    : ''
                }${
                  dashboard.spendVsBudget.projectedMonthEnd != null
                    ? ` · forecast ${formatCurrencyValue(dashboard.spendVsBudget.projectedMonthEnd)}`
                    : ''
                }${
                  // Say what the comparison LEAVES OUT. The budget is the sum of
                  // the accounts that have one, and the MTD beside it is now the
                  // spend of those same accounts - correct, but it means the tile
                  // can read "on pace" while unbudgeted accounts spend freely.
                  // Naming that spend is the difference between a true number and
                  // a useful one.
                  dashboard.spendVsBudget.budgetCoverageKnown &&
                  (dashboard.spendVsBudget.unbudgetedSpend ?? 0) > 0
                    ? ` · excludes ${formatCurrencyValue(dashboard.spendVsBudget.unbudgetedSpend ?? 0)} in unbudgeted accounts`
                    : ''
                }`
              : undefined
          }
          trend={
            dashboard.spendVsBudget.budgetConfigured &&
            dashboard.spendVsBudget.variancePercentage != null &&
            dashboard.spendVsBudget.variance != null
              ? {
                  value: Math.abs(dashboard.spendVsBudget.variancePercentage),
                  direction: dashboard.spendVsBudget.variance < 0 ? 'down' : 'up',
                  // "pace" not "budget": this compares month-to-date spend with
                  // the budget PRO-RATED to days elapsed. Saying "under budget"
                  // for a raw MTD-vs-full-month comparison is what made the tile
                  // read reassuringly green while spending was genuinely over.
                  label: dashboard.spendVsBudget.variance < 0 ? 'under pace (MTD)' : 'over pace (MTD)',
                }
              : undefined
          }
          variant={
            !dashboard.spendVsBudget.budgetConfigured
              ? 'default'
              : dashboard.spendVsBudget.varianceStatus === 'safe'
              ? 'success'
              : dashboard.spendVsBudget.varianceStatus === 'warning'
              ? 'warning'
              : 'danger'
          }
          // No budget action on this card. Budgets are configured exclusively in
          // Admin Console -> Budget Settings: they are set per account, with
          // provider and tenant totals derived as sums. That is administration,
          // not something to do from an executive summary. This card reports the
          // number and neither edits it nor links away.
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
        />

        <MetricCard
          title="Month-over-Month"
          info="This month so far versus the same number of days last month. Comparing a part-month against a whole one would always look like a fall, so both sides cover the same day range."
          value={formatCurrencyValue(dashboard.momTrend.currentMonth)}
          subtitle={`Previous: ${formatCurrencyValue(dashboard.momTrend.previousMonth)}${
            dashboard.momTrend.isPreviousMonthEstimated ? ' (estimated)' : ''
          }`}
          trend={{
            value: Math.abs(dashboard.momTrend.changePercentage),
            direction: dashboard.momTrend.trend,
            label: 'vs last month',
          }}
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              />
            </svg>
          }
        />

        <MetricCard
          title="Potential Savings"
          info="Total monthly saving available from every open recommendation. These are opportunities identified, not money saved yet - overlapping findings on the same resource are de-duplicated so this figure is not inflated."
          value={formatCurrencyValue(dashboard.realizedSavings.totalSavings)}
          subtitle={`${dashboard.realizedSavings.totalSavings > 0
            ? `${formatPercentage(dashboard.realizedSavings.savingsPercentage)} of spend — optimization opportunities`
            : 'No optimization opportunities detected'}${savedBucket
            ? ` · Saved (proven on the bill): ${formatCurrencyValue(savedBucket.saved_monthly)}/mo from ${savedBucket.count} fix${savedBucket.count === 1 ? '' : 'es'}`
            : ''}`
          }
          variant="success"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
        />

        {forecastingEnabled !== false && (
          <MetricCard
            title="Forecast Accuracy"
          info="How close previous forecasts landed to actual spend. Higher is better; it tells you how much to trust the projected month-end number."
            value={formatPercentage(dashboard.forecastAccuracy.accuracyPercentage)}
            subtitle={
              // Unlike the other three cards (Total Spend, MoM, Realized
              // Savings, all measured actuals), this p50/variance figure is a
              // forward-looking projection, not a settled number - the label
              // makes that explicit instead of presenting a forecast as fact.
              dashboard.forecastAccuracy.p50 && dashboard.forecastAccuracy.p10 && dashboard.forecastAccuracy.p90
                ? `Projected (estimated): ${formatCurrencyValue(dashboard.forecastAccuracy.p50)} (range: ${formatCurrencyValue(dashboard.forecastAccuracy.p10)}-${formatCurrencyValue(dashboard.forecastAccuracy.p90)})${
                    dashboard.forecastAccuracy.confidence
                      ? ` • ${Math.round(dashboard.forecastAccuracy.confidence * 100)}% confidence`
                      : ''
                  }`
                : `Variance (estimated): ${formatCurrencyValue(Math.abs(dashboard.forecastAccuracy.variance))}`
            }
            variant={dashboard.forecastAccuracy.accuracyPercentage >= 90 ? 'success' : 'warning'}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
          />
        )}
      </div>
      )}

      {/* Spend Trend Chart - AC 1: Month-over-month trend */}
      {/* REQ-012-HYBRID: Stacked bars (colored by provider) when viewing "All Providers" */}
      {sections.spendTrendChart && (
        <SpendChart
          data={dashboard.momTrend.monthlyData}
          title="Spend Trend vs Budget"
          showBudget={true}
          activeProvider={selectedProvider}
          providerBreakdown={dashboard.costByProvider}
          currency={dashboard.spendVsBudget.currency}
          // The bars are every account's spend; the dashed line is the sum of
          // the budgets that exist. Say so in the legend when those differ,
          // rather than leaving the viewer to read a partial budget as a breach.
          budgetCoverage={
            dashboard.spendVsBudget.budgetCoverageKnown &&
            dashboard.spendVsBudget.budgetedAccountsWithSpend != null
              ? {
                  budgeted: dashboard.spendVsBudget.budgetedAccountsWithSpend,
                  total: dashboard.costByAccount?.length ?? 0,
                }
              : undefined
          }
        />
      )}

      {/* Two Column Layout */}
      {(sections.savingsBreakdown || sections.wasteBreakdown) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Savings Breakdown - AC 1: Realized savings */}
          {sections.savingsBreakdown && (
            <SavingsBreakdown savings={dashboard.realizedSavings} currency={dashboard.spendVsBudget.currency} />
          )}

          {/* Waste Index - AC 1: Waste index */}
          {sections.wasteBreakdown && (
            <WasteBreakdown wasteIndex={dashboard.wasteIndex} currency={dashboard.spendVsBudget.currency} />
          )}
        </div>
      )}

      {/* Cost by Region */}
      {sections.costByRegion && dashboard.costByRegion && dashboard.costByRegion.length > 0 && (
        <CostByRegion regions={dashboard.costByRegion} currency={dashboard.spendVsBudget.currency} />
      )}

      {/* Cost by Provider - Phase 3: Multi-cloud support */}
      {sections.costByProvider && dashboard.costByProvider && dashboard.costByProvider.length > 0 && (
        <CostByProvider providers={dashboard.costByProvider} currency={dashboard.spendVsBudget.currency} />
      )}

      {/* Cost Breakdown Grid - Service and Account */}
      {(sections.costByService || sections.costByAccount) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Cost by Service */}
          {sections.costByService && dashboard.costByService && dashboard.costByService.length > 0 && (
            <CostByService services={dashboard.costByService} currency={dashboard.spendVsBudget.currency} />
          )}

          {/* Cost by Account */}
          {sections.costByAccount && dashboard.costByAccount && dashboard.costByAccount.length > 0 && (
            <CostByAccount accounts={dashboard.costByAccount} currency={dashboard.spendVsBudget.currency} />
          )}
        </div>
      )}

      {/* Commitment Metrics - AC 1: Commitment coverage/utilization */}
      {sections.commitmentMetrics && <CommitmentMetrics metrics={dashboard.commitmentMetrics} />}

      {/* Top Anomalies - AC 1: Top anomalies */}
      {/* REQ-012: Filter anomalies by selected provider */}
      {sections.topAnomalies && (
        <AnomalyList
          anomalies={dashboard.topAnomalies}
          maxItems={5}
          provider={selectedProvider}
          currency={dashboard.spendVsBudget.currency}
          hiddenCount={dashboard.hiddenAnomalyCount}
        />
      )}
        </>
      )}

    </div>
  );
};

